import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Space,
  Dropdown,
  message,
  Statistic,
  Select,
  InputNumber,
  Typography,
  Alert,
  Steps,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  WarningOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import useStore from '../store/useStore';
import { receivingAPI, purchaseOrdersAPI, locationsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const Receiving = () => {
  const { receivingRecords, setReceivingRecords } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerMode, setDrawerMode] = useState('record'); // 'record' | 'receive'
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [receivingPO, setReceivingPO] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [receiveForm] = Form.useForm();

  const fetchReceivingRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await receivingAPI.getAll();
      setReceivingRecords(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setReceivingRecords]);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      const response = await purchaseOrdersAPI.getAll();
      setPurchaseOrders(response.data.filter(po => ['sent', 'confirmed', 'partial'].includes(po.status)));
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsAPI.getAll();
      setLocations(response.data.filter(loc => loc.active));
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const handleAddReceiving = () => {
    setDrawerMode('record');
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      receivedDate: moment(),
      status: 'pending',
    });
    setDrawerVisible(true);
  };

  const handleReceiveItems = (po) => {
    setDrawerMode('receive');
    setReceivingPO(po);
    receiveForm.resetFields();
    receiveForm.setFieldsValue({
      poId: po.id,
      receivedDate: moment(),
      locationId: locations[0]?.id,
      items: po.items?.map(item => ({
        ...item,
        receivedQuantity: item.quantity - (item.receivedQuantity || 0),
        condition: 'good',
      })) || [],
    });
    setDrawerVisible(true);
  };

  const handleEditRecord = (record) => {
    setDrawerMode('record');
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      receivedDate: moment(record.receivedDate),
    });
    setDrawerVisible(true);
  };

  const handleDeleteRecord = async (recordId) => {
    Modal.confirm({
      title: 'Delete Receiving Record',
      content: 'Are you sure you want to delete this receiving record? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await receivingAPI.delete(recordId);
          message.success('Receiving record deleted successfully');
          await fetchReceivingRecords();
          if (selectedRecord?.id === recordId) {
            setCurrentView('list');
            setSelectedRecord(null);
          }
        } catch (err) {
          message.error('Failed to delete receiving record');
        }
      },
    });
  };

  const handleDrawerSubmit = async () => {
    try {
      setDrawerLoading(true);
      if (drawerMode === 'record') {
        const values = await form.validateFields();
        const formattedValues = {
          ...values,
          receivedDate: values.receivedDate.format('YYYY-MM-DD'),
        };
        if (editingRecord) {
          await receivingAPI.update(editingRecord.id, formattedValues);
          message.success('Receiving record updated successfully');
        } else {
          await receivingAPI.create(formattedValues);
          message.success('Receiving record created successfully');
        }
      } else {
        const values = await receiveForm.validateFields();
        const formattedValues = {
          ...values,
          receivedDate: values.receivedDate.format('YYYY-MM-DD'),
        };
        await receivingAPI.receiveItems(formattedValues);
        message.success('Items received successfully');
        await fetchPurchaseOrders();
      }
      setDrawerVisible(false);
      setEditingRecord(null);
      setReceivingPO(null);
      await fetchReceivingRecords();
    } catch (err) {
      message.error('Failed to save');
    } finally {
      setDrawerLoading(false);
    }
  };

  

  const getReceivingStatus = (record) => {
    switch (record.status) {
      case 'pending':
        return { color: 'orange', text: 'Pending', icon: <ClockCircleOutlined /> };
      case 'partial':
        return { color: 'blue', text: 'Partially Received', icon: <InboxOutlined /> };
      case 'completed':
        return { color: 'green', text: 'Completed', icon: <CheckCircleOutlined /> };
      case 'discrepancy':
        return { color: 'red', text: 'Discrepancy', icon: <WarningOutlined /> };
      default:
        return { color: 'default', text: record.status, icon: <ClockCircleOutlined /> };
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'good': return 'green';
      case 'damaged': return 'red';
      case 'defective': return 'orange';
      default: return 'default';
    }
  };

  const filteredRecords = receivingRecords.filter(record => {
    const matchesSearch = record.poNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
                         record.vendorName?.toLowerCase().includes(searchText.toLowerCase()) ||
                         record.receivingNumber?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'Receiving #',
      dataIndex: 'receivingNumber',
      key: 'receivingNumber',
      render: (receivingNumber, record) => (
        <div>
          <div className="font-medium text-blue-600">{receivingNumber}</div>
          <div className="text-sm text-gray-500">
            {moment(record.receivedDate).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      render: (poNumber) => (
        <div className="font-medium">{poNumber}</div>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName',
    },
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      render: (locationName) => (
        <Tag color="blue">{locationName}</Tag>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.totalItems} items</div>
          <div className="text-sm text-gray-500">
            Received: {record.receivedItems}
          </div>
        </div>
      ),
    },
    {
      title: 'Condition',
      key: 'condition',
      render: (_, record) => (
        <div className="space-y-1">
          {record.conditions?.map((condition, index) => (
            <Tag key={index} color={getConditionColor(condition.type)}>
              {condition.count} {condition.type}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const status = getReceivingStatus(record);
        return (
          <Tag color={status.color} icon={status.icon}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View Details',
                icon: <SearchOutlined />,
                onClick: () => {
                  setSelectedRecord(record);
                  setCurrentView('detail');
                },
              },
              {
                key: 'edit',
                label: 'Edit Record',
                icon: <EditOutlined />,
                onClick: () => handleEditRecord(record),
              },
              {
                key: 'delete',
                label: 'Delete Record',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleDeleteRecord(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchReceivingRecords();
    fetchPurchaseOrders();
    fetchLocations();
  }, [fetchReceivingRecords, fetchPurchaseOrders, fetchLocations]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading receiving records..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchReceivingRecords}
      />
    );
  }

  const renderReceivingList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Receiving</h1>
          <p className="text-gray-600">Manage inventory receiving and inspection</p>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<InboxOutlined />}
            onClick={handleAddReceiving}
          >
            New Receiving
          </Button>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Receipts"
              value={receivingRecords.length}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending"
              value={receivingRecords.filter(r => r.status === 'pending').length}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Completed"
              value={receivingRecords.filter(r => r.status === 'completed').length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Discrepancies"
              value={receivingRecords.filter(r => r.status === 'discrepancy').length}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          {/* Search and Filters + Table */}
          <Card>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Search
                placeholder="Search receiving records..."
                allowClear
                onSearch={handleSearch}
                onChange={(e) => handleSearch(e.target.value)}
                className="sm:w-80"
              />
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={handleStatusFilter}
                className="w-full sm:w-40"
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="partial">Partial</Option>
                <Option value="completed">Completed</Option>
                <Option value="discrepancy">Discrepancy</Option>
              </Select>
            </div>

            <Table
              columns={columns}
              dataSource={filteredRecords}
              rowKey="id"
              rowSelection={rowSelection}
              pagination={{
                total: filteredRecords.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} receiving records`,
              }}
              scroll={{ x: 800 }}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRecord(record);
                  setCurrentView('detail');
                },
              })}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          {/* Pending Purchase Orders - Right Column */}
          {purchaseOrders.length > 0 && (
            <Card title="Pending Purchase Orders">
              <Alert
                message="Purchase orders ready for receiving"
                description="The following purchase orders are awaiting receipt of items."
                type="info"
                showIcon
                className="mb-4"
              />
              <div className="space-y-3">
                {purchaseOrders.map(po => (
                  <Card key={po.id} size="small" className="border-l-4 border-l-blue-500">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">PO #{po.poNumber}</div>
                          <div className="text-sm text-gray-500">{po.vendorName}</div>
                        </div>
                        <Tag color="blue">{po.status}</Tag>
                      </div>
                      <div className="text-sm">
                        Expected: {moment(po.expectedDate).format('MMM DD, YYYY')}
                      </div>
                      <Button
                        type="primary"
                        size="small"
                        icon={<TruckOutlined />}
                        onClick={() => handleReceiveItems(po)}
                        block
                      >
                        Receive Items
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );

  const renderReceivingDetail = () => {
    if (!selectedRecord) return null;
    const status = getReceivingStatus(selectedRecord);
    return (
      <DetailView
        title={`Receiving #${selectedRecord.receivingNumber}`}
        subtitle={`PO #${selectedRecord.poNumber} • ${selectedRecord.vendorName}`}
        tags={[
          <Tag color={status.color} icon={status.icon} key="status">{status.text}</Tag>,
          <Tag color="blue" key="location">{selectedRecord.locationName}</Tag>,
        ]}
        onBack={() => {
          setCurrentView('list');
          setSelectedRecord(null);
        }}
        actions={[
          { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => handleEditRecord(selectedRecord) },
          { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true, onClick: () => handleDeleteRecord(selectedRecord.id) },
        ]}
      >
        <Row gutter={24}>
          <Col xs={24} md={12} lg={8}>
            <DetailSection title="Summary">
              <DetailField label="Receiving #" value={selectedRecord.receivingNumber} />
              <DetailField label="Received Date" value={moment(selectedRecord.receivedDate).format('MMM DD, YYYY')} />
              <DetailField label="Status" value={status.text} />
              <DetailField label="PO Number" value={selectedRecord.poNumber} />
              <DetailField label="Vendor" value={selectedRecord.vendorName} />
              <DetailField label="Location" value={selectedRecord.locationName} />
            </DetailSection>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <DetailSection title="Items">
              <DetailField label="Total Items" value={selectedRecord.totalItems} />
              <DetailField label="Received Items" value={selectedRecord.receivedItems} />
              <div className="space-x-2 mt-2">
                {selectedRecord.conditions?.map((c, i) => (
                  <Tag key={i} color={getConditionColor(c.type)}>{c.count} {c.type}</Tag>
                ))}
              </div>
            </DetailSection>
          </Col>
          <Col xs={24} md={24} lg={8}>
            <DetailSection title="Notes">
              <DetailField value={selectedRecord.notes || '-'} />
            </DetailSection>
          </Col>
        </Row>
      </DetailView>
    );
  };

  const renderDrawerForm = () => (
    <RightDrawer
      title={drawerMode === 'record' ? (editingRecord ? 'Edit Receiving Record' : 'New Receiving Record') : `Receive Items - PO #${receivingPO?.poNumber}`}
      open={drawerVisible}
      onClose={() => setDrawerVisible(false)}
      onSubmit={handleDrawerSubmit}
      loading={drawerLoading}
      formId="drawer-form"
      width={drawerMode === 'receive' ? 800 : 600}
    >
      {drawerMode === 'record' ? (
        <Form id="drawer-form" form={form} layout="vertical" className="mt-2">
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="poId"
                label="Purchase Order"
                type="select"
                options={purchaseOrders.map(po => ({ value: po.id, label: `${po.poNumber} - ${po.vendorName}` }))}
                rules={[{ required: true, message: 'Please select purchase order' }]}
                placeholder="Select purchase order"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="receivingNumber"
                label="Receiving Number"
                rules={[{ required: true, message: 'Please enter receiving number' }]}
                placeholder="Enter receiving number"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="receivedDate"
                label="Received Date"
                type="date"
                rules={[{ required: true, message: 'Please select received date' }]}
              />
            </Col>
            <Col span={12}>
              <FormField
                name="locationId"
                label="Location"
                type="select"
                options={locations.map(location => ({ value: location.id, label: location.name }))}
                rules={[{ required: true, message: 'Please select location' }]}
                placeholder="Select location"
              />
            </Col>
          </Row>
          <FormField name="notes" label="Notes" type="textarea" placeholder="Enter any notes about the receiving" rows={3} />
          <FormField
            name="status"
            label="Status"
            type="select"
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'partial', label: 'Partially Received' },
              { value: 'completed', label: 'Completed' },
              { value: 'discrepancy', label: 'Discrepancy' },
            ]}
            rules={[{ required: true, message: 'Please select status' }]}
          />
        </Form>
      ) : (
        <Form id="drawer-form" form={receiveForm} layout="vertical" className="mt-2">
          <Steps
            current={0}
            items={[
              { title: 'Scan/Count', icon: <ScanOutlined /> },
              { title: 'Inspect', icon: <CheckCircleOutlined /> },
              { title: 'Store', icon: <InboxOutlined /> },
            ]}
            className="mb-6"
          />
          <Row gutter={16}>
            <Col span={12}>
              <FormField name="receivedDate" label="Received Date" type="date" rules={[{ required: true, message: 'Please select received date' }]} />
            </Col>
            <Col span={12}>
              <FormField
                name="locationId"
                label="Storage Location"
                type="select"
                options={locations.map(location => ({ value: location.id, label: location.name }))}
                rules={[{ required: true, message: 'Please select location' }]}
                placeholder="Select storage location"
              />
            </Col>
          </Row>
          <div className="border rounded-lg p-4 bg-gray-50">
            <Text strong className="block mb-3">Items to Receive:</Text>
            {receivingPO?.items?.map((item, index) => (
              <div key={index} className="bg-white p-3 rounded border mb-2">
                <Row gutter={16} align="middle">
                  <Col span={8}>
                    <Text strong>{item.productName}</Text>
                    <div className="text-sm text-gray-500">Expected: {item.quantity}</div>
                  </Col>
                  <Col span={4}>
                    <Form.Item name={['items', index, 'receivedQuantity']} label="Received" className="mb-0">
                      <InputNumber min={0} max={item.quantity} placeholder="0" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['items', index, 'condition']} label="Condition" className="mb-0">
                      <Select placeholder="Select condition">
                        <Option value="good">Good</Option>
                        <Option value="damaged">Damaged</Option>
                        <Option value="defective">Defective</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['items', index, 'notes']} label="Notes" className="mb-0">
                      <Input placeholder="Optional notes" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
          <FormField name="receivingNotes" label="Receiving Notes" type="textarea" placeholder="Enter any notes about this receiving" rows={3} />
        </Form>
      )}
    </RightDrawer>
  );

  return (
    <div className="space-y-6">
      {currentView === 'list' ? renderReceivingList() : renderReceivingDetail()}
      {renderDrawerForm()}
    </div>
  );
};

export default Receiving;
